// src/components/layout/MainLayout.tsx
import React, { useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { useSidebarStore } from '../../store/sidebarStore';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/auth';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { getAssetUrl } from '../../utils/url';

const MainLayout: React.FC = () => {
  const { isExpanded } = useSidebarStore();
  const { user, token, setUser, loadUser } = useAuthStore();
  const { settings, fetchSettings, isHydrated } = useSystemSettingsStore();
  const navigate = useNavigate();

  // Ref to track if settings have been fetched
  const settingsFetched = useRef(false);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Fetch settings only once
  useEffect(() => {
    if (!settingsFetched.current) {
      settingsFetched.current = true;
      fetchSettings();
    }
  }, [fetchSettings]);

  // Update document title & favicon ONLY when settings change
  useEffect(() => {
    // Update title
    if (settings.app_name) {
      document.title = settings.app_name;
    }

    // Update favicon
    if (settings.favicon_url) {
      const faviconUrl = getAssetUrl(settings.favicon_url);
      if (faviconUrl) {
        updateFavicon(faviconUrl);
      }
    }
  }, [settings.app_name, settings.favicon_url]);

  useEffect(() => {
    const fetchUser = async () => {
      if (token && !user) {
        try {
          const userData = await authService.getUser();
          setUser(userData);
        } catch (error) {
          console.error('Failed to fetch user:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        }
      }
    };
    fetchUser();
  }, [token, user, setUser, navigate]);

  return (
    <div className="min-h-screen bg-neutral-bg">
      <Sidebar />
      <Navbar />
      <main
        className={`
          pt-16 min-h-screen transition-all duration-300
          ${isExpanded ? 'lg:ml-64' : 'lg:ml-16'}
        `}
      >
        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

// Helper function to update favicon without duplicate elements
const updateFavicon = (url: string) => {
  // Remove existing favicons
  const existingLinks = document.querySelectorAll("link[rel*='icon']");
  existingLinks.forEach(link => (link as HTMLElement).remove());

  // Add new favicon
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/png';
  link.href = url;
  document.head.appendChild(link);

  // Also add apple-touch-icon
  const appleLink = document.createElement('link');
  appleLink.rel = 'apple-touch-icon';
  appleLink.href = url;
  document.head.appendChild(appleLink);
};

export default MainLayout;
